import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Container } from "@/components/site/Container";
import { BlogPostCard } from "@/components/blog/BlogPostCard";
import { BlogSidebar } from "@/components/blog/BlogSidebar";
import {
  getCategoryById,
  listPublishedPosts,
} from "@/lib/blog.functions";
import { canonical } from "@/lib/seo";

export const Route = createFileRoute("/blog/category/$category")({
  loader: async ({ params, context }) => {
    const cat = await context.queryClient.fetchQuery({
      queryKey: ["blog", "cat", params.category],
      queryFn: async () => {
        const c = await getCategoryById({ data: { id: params.category } });
        if (!c) throw notFound();
        return c;
      },
    });
    return cat;
  },
  component: CategoryPage,
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const title = `${loaderData.name} | Durafoam Roofing Blog`;
    const desc = loaderData.description ?? `${loaderData.name} articles from Durafoam Roofing.`;
    const path = `/blog/category/${params.category}`;
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: canonical("/") },
        { "@type": "ListItem", position: 2, name: "Blog", item: canonical("/blog") },
        { "@type": "ListItem", position: 3, name: loaderData.name, item: canonical(path) },
      ],
    };
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: path },
      ],
      links: [{ rel: "canonical", href: path }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(breadcrumb) },
      ],
    };
  },
  errorComponent: ({ error }) => (
    <div className="p-12 text-center"><p>{error instanceof Error ? error.message : String(error)}</p></div>
  ),
  notFoundComponent: () => (
    <div className="p-12 text-center">
      <h1 className="text-2xl font-bold">Category not found</h1>
      <Link to="/blog" className="mt-4 inline-block text-accent">← Back to blog</Link>
    </div>
  ),
});

function CategoryPage() {
  const { category } = Route.useParams();
  const cat = Route.useLoaderData();
  const listFn = useServerFn(listPublishedPosts);
  const { data } = useSuspenseQuery({
    queryKey: ["blog", "cat-list", category],
    queryFn: () => listFn({ data: { page: 1, pageSize: 24, categoryId: category } }),
  });

  return (
    <section className="bg-[var(--surface)] py-10 lg:py-14">
      <Container>
        <nav className="mb-4 text-xs text-[var(--muted-foreground)]">
          <Link to="/" className="hover:text-primary">Home</Link>
          {" › "}
          <Link to="/blog" className="hover:text-primary">Blog</Link>
          {" › "}
          <span className="text-foreground">{cat.name}</span>
        </nav>
        <h1 className="text-3xl font-bold text-primary lg:text-4xl">{cat.name}</h1>
        {cat.description && (
          <p className="mt-3 max-w-2xl text-base text-[var(--muted-foreground)]">
            {cat.description}
          </p>
        )}
        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px]">
          <div>
            {data.posts.length === 0 ? (
              <p className="text-[var(--muted-foreground)]">No posts in this category yet.</p>
            ) : (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {data.posts.map((p) => (
                  <BlogPostCard key={p.id} post={p} />
                ))}
              </div>
            )}
          </div>
          <BlogSidebar activeCategoryId={category} />
        </div>
      </Container>
    </section>
  );
}
